import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useShipment } from "@/contexts/shipment-context";
import { customerService } from "@/lib/database";
import { DollarSign, Package, Truck, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export interface DashboardStatsProps {
  stats: {
    totalShipments: number;
    activeShipments: number;
    totalCustomers: number;
    revenue: number;
  };
}

export function DashboardStats() {
  const { stats: shipmentStats, loadingStats, refreshStats } = useShipment();
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(true);

  const loadTopCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const customerData = await customerService.getAll(1000);
      setTotalCustomers(customerData?.customers?.length || 0);
    } catch (error) {
      console.error("Error loading customers count:", error);
      setTotalCustomers(0);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const stats: DashboardStatsProps["stats"] = useMemo(
    () => ({
      totalShipments: shipmentStats.totalShipments || 0,
      activeShipments: shipmentStats.activeShipments || 0,
      totalCustomers: totalCustomers || 0,
      revenue: shipmentStats.totalRevenue || 0,
    }),
    [shipmentStats, totalCustomers]
  );

  useEffect(() => {
    refreshStats();
    loadTopCustomers();
  }, [refreshStats]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Shipments</CardTitle>
          <Package className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {stats.totalShipments.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">All time shipments</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Active Shipments
          </CardTitle>
          <Truck className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.activeShipments}</div>
          <p className="text-xs text-muted-foreground">Currently in progress</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalCustomers}</div>
          <p className="text-xs text-muted-foreground">Registered customers</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            ${stats.revenue.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">All time revenue</p>
        </CardContent>
      </Card>
    </div>
  );
}
